import React, { useState, useEffect, useRef, useContext, useCallback } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green, grey } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { List, Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import {
    Users as GroupIcon,
    Check as AcceptIcon,
    CornerUpRight as TransferIcon,
    CircleX as CloseTicketIcon,
    Eye as SpyIcon,
    X as CloseIcon,
    MessageSquare as MessageIcon,
} from "lucide-react";
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import maskPhoneLast4 from "../../utils/maskPhoneLast4";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { 
    Avatar, 
    Badge, 
    ListItemAvatar, 
    ListItem, 
    ListItemSecondaryAction, 
    ListItemText, 
    Typography, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    IconButton, 
    Paper, 
    Divider 
} from "@material-ui/core";
import { blue } from "@material-ui/core/colors";

const useStyles = makeStyles((theme) => ({
    ticket: {
        position: "relative",
        display: "block",
        boxSizing: "border-box",
        cursor: "pointer",
        margin: "6px 8px",
        padding: 12,
        borderRadius: 12,
        transition: "background 0.18s ease, box-shadow 0.18s ease",
        backgroundColor: theme.mode === "light"
            ? "rgba(0,0,0,0.03)"
            : "rgba(255,255,255,0.025)",
        "&:hover": {
            backgroundColor: theme.mode === "light"
                ? "rgba(0,0,0,0.06)"
                : "rgba(255,255,255,0.06)",
        },
        "&.is-selected, &.is-selected:hover": {
            backgroundColor: theme.mode === "light"
                ? `color-mix(in srgb, ${theme.palette.primary.main} 10%, transparent)`
                : `color-mix(in srgb, ${theme.palette.primary.main} 18%, transparent)`,
            boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
        },
    },
    ticketInner: {
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: 0,
    },
    photoBox: {
        flex: "0 0 auto",
        padding: 0,
        display: "flex",
        alignItems: "center",
    },
    contentBox: {
        flex: "1 1 auto",
        minWidth: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
    },
    rightBox: {
        flex: "0 0 auto",
        padding: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 4,
        minWidth: 90,
    },
    rightActionsRow: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 4,
    },
    headerLine: {
        display: "flex",
        alignItems: "center",
        gap: 4,
        minWidth: 0,
    },
    messageLine: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 6,
        minWidth: 0,
    },

    pendingTicket: {
        cursor: "unset",
    },
    queueTag: {
        background: "#FCFCFC",
        color: "#000",
        marginRight: 1,
        padding: 1,
        fontWeight: 'bold',
        borderRadius: 3,
        fontSize: "0.5em",
        whiteSpace: "nowrap"
    },
    noTicketsDiv: {
        display: "flex",
        height: "100px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
    newMessagesCount: {
        justifySelf: "flex-end",
        textAlign: "right",
        position: "relative",
        top: 0,
        color: "green",
        fontWeight: "bold",
        marginRight: "10px",
        borderRadius: 0,
    },
    noTicketsText: {
        textAlign: "center",
        color: "rgb(146, 104, 104)",
        fontSize: "14px",
        lineHeight: "1.4",
    },
    connectionTag: {
        background: "#05ab92",
        color: "#FFF",
        marginRight: 4,
        padding: "2px 8px",
        fontWeight: 600,
        borderRadius: 999,
        fontSize: "0.68em",
        letterSpacing: 0.3,
        whiteSpace: "nowrap",
        height: 18,
        lineHeight: "14px",
        display: "inline-flex",
        alignItems: "center",
    },
    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    contactNameWrapper: {
        display: "flex",
        justifyContent: "space-between",
        marginLeft: "5px",
        fontWeight: 600,
        color: theme.mode === 'light' ? "#111b21" : "#e9edef",
    },

    lastMessageTime: {
        textAlign: "right",
        fontSize: "0.72rem",
        color: theme.mode === 'light' ? "#667781" : "#8696a0",
    },

    lastMessageTimeUnread: {
        textAlign: "right",
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: "0.72rem",
    },

    closedBadge: {
        alignSelf: "center",
        justifySelf: "flex-end",
        marginRight: 32,
        marginLeft: "auto",
    },

    contactLastMessage: {
        paddingRight: "0%",
        marginLeft: "5px",
        fontSize: "0.82rem",
        color: theme.mode === 'light' ? "#667781" : "#8696a0",
    },

    draftLabel: {
        color: "#e65100",
        fontWeight: 600,
        fontSize: "0.85em",
        marginRight: 4,
    },

    contactLastMessageUnread: {
        paddingRight: 20,
        fontWeight: 600,
        fontSize: "0.82rem",
        color: theme.mode === 'light' ? "#111b21" : "#e9edef",
        width: "50%"
    },

    badgeStyle: {
        color: "white",
        backgroundColor: blue[500],
        fontWeight: 700,
        minWidth: 18,
        height: 18,
        padding: "0 5px",
        position: "static",
        transform: "none",
        boxShadow: theme.mode === "light"
            ? "0 1px 2px rgba(0,0,0,0.15)"
            : "0 1px 2px rgba(0,0,0,0.5)",
    },

    acceptButton: {
        position: "absolute",
        right: "1px",
    },

    ticketQueueColor: {
        flex: "none",
        height: "100%",
        position: "absolute",
        top: "0%",
        left: "0%",
    },

    ticketInfo: {
        position: "relative",
        top: -13
    },
    secondaryContentSecond: {
        display: 'flex',
        alignItems: "center",
        flexWrap: "wrap",
        flexDirection: "row",
        alignContent: "flex-start",
        gap: "2px",
    },
    cardInner: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 4,
        marginLeft: 5,
        marginTop: 2,
    },
    tagsRow: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 4,
        marginTop: 2,
    },
    ticketInfo1: {
        position: "relative",
        top: 13,
        right: 0
    },
    Radiusdot: {
        "& .MuiBadge-badge": {
            borderRadius: 2,
            position: "inherit",
            height: 16,
            margin: 2,
            padding: 3
        },
        "& .MuiBadge-anchorOriginTopRightRectangle": {
            transform: "scale(1) translate(0%, -40%)",
        },
    },
    connectionIcon: {
        marginRight: theme.spacing(1)
    },
    dialogTitle: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: theme.palette.primary.main,
        color: "white",
        paddingBottom: theme.spacing(1),
    },
    closeButton: {
        color: "white",
    },
    messagesContainer: {
        height: "60vh", // Use viewport height instead of fixed pixels
        maxHeight: "600px", // Set a maximum height
        overflowY: "auto",
        padding: theme.spacing(2),
        scrollBehavior: "smooth", // Add smooth scrolling
    },
    scrollToBottomBtn: {
        position: "absolute",
        bottom: theme.spacing(2),
        right: theme.spacing(2),
        zIndex: 1000,
        backgroundColor: theme.palette.primary.main,
        color: "white",
        "&:hover": {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    messageItem: {
        padding: theme.spacing(1),
        margin: theme.spacing(1, 0),
        borderRadius: theme.spacing(1),
        maxWidth: "80%",
        position: "relative",
    },
    fromMe: {
        backgroundColor: "#dcf8c6",
        marginLeft: "auto",
    },
    fromThem: {
        backgroundColor: "#f5f5f5",
    },
    messageTime: {
        fontSize: "0.75rem",
        color: grey[500],
        position: "absolute",
        bottom: "2px",
        right: "8px",
    },
    messageText: {
        marginBottom: theme.spacing(2),
        wordBreak: "break-word",
    },
    emptyMessages: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        color: grey[500],
    },
    messagesHeader: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.grey[100],
    },
    messageAvatar: {
        marginRight: theme.spacing(1),
    },
    messageIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    loadingMessages: {
        display: "flex",
        justifyContent: "center",
        padding: theme.spacing(3),
    },
    unrepliedDot: {
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: "#ff9800",
        display: "inline-block",
        marginRight: 6,
        flexShrink: 0,
        animation: "$pulse 1.5s ease-in-out infinite",
    },
    "@keyframes pulse": {
        "0%": { opacity: 1, transform: "scale(1)" },
        "50%": { opacity: 0.4, transform: "scale(0.8)" },
        "100%": { opacity: 1, transform: "scale(1)" },
    },
}));

const TicketListItemCustom = ({ setTabOpen, ticket, showTags = true }) => {
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
    const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);

    const [openAlert, setOpenAlert] = useState(false);
    const [userTicketOpen, setUserTicketOpen] = useState("");
    const [queueTicketOpen, setQueueTicketOpen] = useState("");
    const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
    
    // New states for the ticket messages
    const [ticketMessages, setTicketMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);

    const { ticketId } = useParams();
    const isMounted = useRef(true);
    const { setCurrentTicket } = useContext(TicketsContext);
    const { user } = useContext(AuthContext);

    const { get: getSetting } = useCompanySettings();

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
        setAcceptTicketWithouSelectQueueOpen(true);
    }, []);

    const handleCloseTicket = async (id) => {
        const setting = await getSetting(
            {
                "column": "requiredTag"
            }
        );

        if (setting.requiredTag === "enabled") {
            //verificar se tem uma tag   
            try {
                const contactTags = await api.get(`/contactTags/${ticket.contact.id}`);
                if (!contactTags.data.tags) {
                    toast.warning(i18n.t("messagesList.header.buttons.requiredTag"))
                } else {
                    await api.put(`/tickets/${id}`, {
                        status: "closed",
                        userId: user?.id || null,
                    });

                    if (isMounted.current) {
                        setLoading(false);
                    }

                    history.push(`/tickets/`);
                }
            } catch (err) {
                setLoading(false);
                toastError(err);
            }
        } else {
            setLoading(true);
            try {
                await api.put(`/tickets/${id}`, {
                    status: "closed",
                    userId: user?.id || null,
                });

            } catch (err) {
                setLoading(false);
                toastError(err);
            }
            if (isMounted.current) {
                setLoading(false);
            }

            history.push(`/tickets/`);
        }
    };

    const handleCloseIgnoreTicket = async (id) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${id}`, {
                status: "closed",
                userId: user?.id || null,
                sendFarewellMessage: false,
                amountUsedBotQueues: 0
            });

        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }

        history.push(`/tickets/`);
    };

    const truncate = (str, len) => {
        if (!isNil(str)) {
            if (str.length > len) {
                return str.substring(0, len) + "...";
            }
            return str;
        }
    };

    const handleCloseTransferTicketModal = useCallback(() => {
        if (isMounted.current) {
            setTransferTicketModalOpen(false);
        }
    }, []);

    const handleOpenTransferModal = () => {
        setLoading(true)
        setTransferTicketModalOpen(true);
        if (isMounted.current) {
            setLoading(false);
        }
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
    }

    const handleAcepptTicket = async (id) => {
        setLoading(true);
        try {
            const otherTicket = await api.put(`/tickets/${id}`, ({
                status: ticket.isGroup && ticket.channel === 'whatsapp' ? "group" : "open",
                userId: user?.id,
                queueId: ticket?.queueId || ticket?.queue?.id || null,
            }));

            if (otherTicket.data.id !== ticket.id) {
                if (otherTicket.data.userId !== user?.id) {
                    setOpenAlert(true);
                    setUserTicketOpen(otherTicket.data.user.name);
                    setQueueTicketOpen(otherTicket.data.queue.name);
                } else {
                    setLoading(false);
                    setTabOpen(ticket.isGroup ? "group" : "open");
                    handleSelectTicket(otherTicket.data);
                    history.push(`/tickets/${otherTicket.uuid}`);
                }
            } else {
                let setting;

                try {
                    setting = await getSetting({
                        "column": "sendGreetingAccepted"
                    });
                } catch (err) {
                    toastError(err);
                }

                if (setting.sendGreetingAccepted === "enabled" && (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")) {
                    handleSendMessage(ticket.id);
                }
                if (isMounted.current) {
                    setLoading(false);
                }

                setTabOpen(ticket.isGroup ? "group" : "open");
                handleSelectTicket(ticket);
                history.push(`/tickets/${ticket.uuid}`);
            }
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
    };

    const handleSendMessage = async (id) => {
        let setting;

        try {
            setting = await getSetting({
                "column": "greetingAcceptedMessage"
            })
        } catch (err) {
            toastError(err);
        }

        const msg = `${setting.greetingAcceptedMessage}`;
        const message = {
            read: 1,
            fromMe: true,
            mediaUrl: "",
            body: `${msg.trim()}`,
        };
        try {
            await api.post(`/messages/${id}`, message);
        } catch (err) {
            toastError(err);
        }
    };

    const handleCloseAlert = useCallback(() => {
        setOpenAlert(false);
        setLoading(false);
    }, []);

    const handleSelectTicket = (ticket) => {
        const code = uuidv4();
        const { id, uuid } = ticket;
        setCurrentTicket({ id, uuid, code });
    };

    // Function to fetch messages for the ticket
    const fetchTicketMessages = async (ticketId) => {
        if (!ticketId) return;
        
        setLoadingMessages(true);
        try {
            const { data } = await api.get(`/messages/${ticketId}`);
            if (isMounted.current) {
                setTicketMessages(data.messages);
            }
        } catch (err) {
            toastError(err);
        } finally {
            setLoadingMessages(false);
        }
    };

    // Handle opening the message dialog
    const handleOpenMessageDialog = (e) => {
        e.stopPropagation();
        setOpenTicketMessageDialog(true);
        fetchTicketMessages(ticket.id);
    };

    return (
        <React.Fragment key={ticket.id}>
            {openAlert && (
                <ShowTicketOpen
                    isOpen={openAlert}
                    handleClose={handleCloseAlert}
                    user={userTicketOpen}
                    queue={queueTicketOpen}
                />
            )}
            {acceptTicketWithouSelectQueueOpen && (
                <AcceptTicketWithouSelectQueue
                    modalOpen={acceptTicketWithouSelectQueueOpen}
                    onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
                    ticketId={ticket.id}
                    ticket={ticket}
                />
            )}
            {transferTicketModalOpen && (
                <TransferTicketModalCustom
                    modalOpen={transferTicketModalOpen}
                    onClose={handleCloseTransferTicketModal}
                    ticketid={ticket.id}
                    ticket={ticket}
                />
            )}
            
            {/* Improved Message Dialog */}
            <Dialog 
                open={openTicketMessageDialog} 
                onClose={() => setOpenTicketMessageDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle disableTypography className={classes.dialogTitle}>
                    <Typography variant="h6">
                        {i18n.t("ticketsResponsive.dialog.spyingConversation")}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        className={classes.closeButton}
                        onClick={() => setOpenTicketMessageDialog(false)}
                    >
                        <CloseIcon size={20} />
                    </IconButton>
                </DialogTitle>
                
                <div className={classes.messagesHeader}>
                    <Avatar
                        src={ticket?.contact?.urlPicture}
                        className={classes.messageAvatar}
                    />
                    <div>
                        <Typography variant="subtitle1">
                            {maskPhoneLast4(ticket.contact?.name)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {ticket.whatsapp?.name || ticket.channel}
                        </Typography>
                    </div>
                </div>
                
                <Divider />
                
                <DialogContent className={classes.messagesContainer}>
                    {loadingMessages ? (
                        <div className={classes.loadingMessages}>
                            <Typography>{i18n.t("ticketsResponsive.dialog.loadingMessages")}</Typography>
                        </div>
                    ) : ticketMessages.length === 0 ? (
                        <div className={classes.emptyMessages}>
                            <MessageIcon size={36} />
                            <Typography variant="body1">
                                {i18n.t("ticketsList.noMessages")}
                            </Typography>
                        </div>
                    ) : (
                        ticketMessages.map((message) => (
                            <Paper 
                                key={message.id} 
                                className={clsx(
                                    classes.messageItem, 
                                    message.fromMe ? classes.fromMe : classes.fromThem
                                )}
                                elevation={0}
                            >
                                <Typography className={classes.messageText}>
                                    {message.body.includes('data:image/png;base64') ? (
                                        <MarkdownWrapper>{i18n.t("messagesResponsive.types.location")}</MarkdownWrapper>
                                    ) : message.body.includes('BEGIN:VCARD') ? (
                                        <MarkdownWrapper>{i18n.t("messagesResponsive.types.contact")}</MarkdownWrapper>
                                    ) : (
                                        <MarkdownWrapper>{message.body}</MarkdownWrapper>
                                    )}
                                </Typography>
                                <Typography variant="caption" className={classes.messageTime}>
                                    {format(parseISO(message.createdAt), "HH:mm")}
                                </Typography>
                            </Paper>
                        ))
                    )}
                </DialogContent>
            </Dialog>
            
            <div
                onClick={(e) => {
                    const tag = e.target.tagName.toLowerCase();
                    if (tag === 'svg' || tag === 'path' || (tag === 'input' && e.target.type === 'checkbox')) return;
                    handleSelectTicket(ticket);
                }}
                className={clsx(classes.ticket, {
                    [classes.pendingTicket]: ticket.status === "pending",
                    "is-selected": ticketId && ticketId === ticket.uuid,
                })}
            >
                <div className={classes.ticketInner}>
                    <div className={classes.photoBox}>
                        <Avatar
                            style={{
                                width: "46px",
                                height: "46px",
                                borderRadius: "50%",
                                border: theme.mode === "light"
                                    ? "2px solid rgba(0,0,0,0.05)"
                                    : "2px solid rgba(255,255,255,0.06)",
                            }}
                            src={`${ticket?.contact?.urlPicture}`}
                        />
                    </div>

                    <div className={classes.contentBox}>
                        <div className={classes.headerLine}>
                            {ticket.fromMe === false && ticket.status === "open" && (
                                <Tooltip title={i18n.t("ticketsResponsive.filter.unreplied")}>
                                    <span className={classes.unrepliedDot} />
                                </Tooltip>
                            )}
                            {ticket.isGroup && ticket.channel === "whatsapp" && (
                                <GroupIcon size={16} style={{ color: theme.mode === "light" ? grey[700] : grey[400], flexShrink: 0 }} />
                            )}
                            {ticket.channel && (
                                <ConnectionIcon width="18" height="18" className={classes.connectionIcon} connectionType={ticket.channel} />
                            )}
                            <Typography
                                noWrap
                                component="span"
                                variant="body2"
                                style={{ fontWeight: 600, color: theme.mode === "light" ? "#111b21" : "#e9edef", flex: 1, minWidth: 0 }}
                            >
                                {truncate(maskPhoneLast4(ticket.contact?.name), 60)}
                            </Typography>
                        </div>

                        <div className={classes.messageLine}>
                            <Typography
                                className={Number(ticket.unreadMessages) > 0 ? classes.contactLastMessageUnread : classes.contactLastMessage}
                                noWrap
                                component="span"
                                variant="body2"
                                style={{ flex: 1, minWidth: 0, marginLeft: 0 }}
                            >
                                {ticket.draft ? (
                                    <>
                                        <span className={classes.draftLabel}>{i18n.t("ticketsList.draft")}</span>
                                        <span style={{ fontStyle: "italic" }}>{truncate(ticket.draft, 30)}</span>
                                    </>
                                ) : ticket.lastMessage ? (
                                    ticket.lastMessage.includes('fb.me') ? (
                                        <MarkdownWrapper>{i18n.t("messagesResponsive.ads.adClick")}</MarkdownWrapper>
                                    ) : ticket.lastMessage.includes('data:image/png;base64') ? (
                                        <MarkdownWrapper>{i18n.t("messagesResponsive.types.location")}</MarkdownWrapper>
                                    ) : ticket.lastMessage.includes('BEGIN:VCARD') ? (
                                        <MarkdownWrapper>{i18n.t("messagesResponsive.types.contact")}</MarkdownWrapper>
                                    ) : (
                                        <MarkdownWrapper>{truncate(ticket.lastMessage, 40)}</MarkdownWrapper>
                                    )
                                ) : null}
                            </Typography>
                        </div>

                        {showTags && (
                            <div className={classes.tagsRow}>
                                {ticket?.whatsapp && (
                                    <Badge
                                        className={classes.connectionTag}
                                        style={{ backgroundColor: ticket.channel === "whatsapp" ? "#05ab92" : ticket.channel === "facebook" ? "#4267B2" : "#E1306C" }}
                                    >
                                        {ticket.whatsapp?.name.toUpperCase()}
                                    </Badge>
                                )}
                                <Badge
                                    style={{ backgroundColor: ticket.queue?.color || "#7c7c7c" }}
                                    className={classes.connectionTag}
                                >
                                    {ticket.queueId ? ticket.queue?.name.toUpperCase() : ticket.status === "lgpd" ? "LGPD" : i18n.t("ticketsResponsive.status.noQueue")}
                                </Badge>
                                {ticket?.user && (
                                    <Badge style={{ backgroundColor: "#055bab" }} className={classes.connectionTag}>
                                        {ticket.user?.name.toUpperCase()}
                                    </Badge>
                                )}
                                {ticket.tags && ticket.tags.length > 0 && ticket.tags.map((tag) => (
                                    <Badge style={{ backgroundColor: tag.color || "#999" }} className={classes.connectionTag} key={`ticket-tag-${ticket.id}-${tag.id}`}>
                                        {tag.name.toUpperCase()}
                                    </Badge>
                                ))}
                                {ticket.contact?.tags && ticket.contact.tags.length > 0 && ticket.contact.tags.map((tag) => (
                                    <Badge style={{ backgroundColor: tag.color || "#999" }} className={classes.connectionTag} key={`contact-tag-${ticket.id}-${tag.id}`}>
                                        {tag.name.toUpperCase()}
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className={classes.rightBox}>
                        {ticket.lastMessage && (
                            <Typography
                                className={Number(ticket.unreadMessages) > 0 ? classes.lastMessageTimeUnread : classes.lastMessageTime}
                                component="span"
                                variant="body2"
                                style={{ position: "static", top: "auto" }}
                            >
                                {isSameDay(parseISO(ticket.updatedAt), new Date())
                                    ? format(parseISO(ticket.updatedAt), "HH:mm")
                                    : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
                            </Typography>
                        )}
                        {Number(ticket.unreadMessages) > 0 && (
                            <Badge
                                badgeContent={ticket.unreadMessages}
                                classes={{ badge: classes.badgeStyle }}
                                style={{ position: "static", marginRight: 8, marginTop: 4, marginBottom: 4 }}
                            />
                        )}
                        <div className={classes.rightActionsRow}>
                            {(ticket.status === "pending" && (ticket.queueId === null || ticket.queueId === undefined)) && (
                                <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                                    <IconButton size="small" disabled={loading} onClick={() => handleOpenAcceptTicketWithouSelectQueue()} style={{ color: theme.mode === "light" ? "#0872B9" : "#FFF", padding: 4 }}>
                                        <AcceptIcon size={18} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {(ticket.status === "pending" && ticket.queueId !== null) && (
                                <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                                    <IconButton size="small" disabled={loading} onClick={() => handleAcepptTicket(ticket.id)} style={{ color: theme.mode === "light" ? "#0872B9" : "#FFF", padding: 4 }}>
                                        <AcceptIcon size={18} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {(ticket.status === "pending" || ticket.status === "open" || ticket.status === "group") && (
                                <Tooltip title={`${i18n.t("ticketsList.buttons.transfer")}`}>
                                    <IconButton size="small" disabled={loading} onClick={handleOpenTransferModal} style={{ color: theme.mode === "light" ? "#0872B9" : "#FFF", padding: 4 }}>
                                        <TransferIcon size={18} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {(ticket.status === "open" || ticket.status === "group") && (
                                <Tooltip title={`${i18n.t("ticketsList.buttons.closed")}`}>
                                    <IconButton size="small" disabled={loading} onClick={() => handleCloseTicket(ticket.id)} style={{ color: theme.mode === "light" ? "#0872B9" : "#FFF", padding: 4 }}>
                                        <CloseTicketIcon size={18} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {((ticket.status === "pending" || ticket.status === "lgpd") && (user.userClosePendingTicket === "enabled" || user.profile === "admin")) && (
                                <Tooltip title={`${i18n.t("ticketsList.buttons.ignore")}`}>
                                    <IconButton size="small" disabled={loading} onClick={() => handleCloseIgnoreTicket(ticket.id)} style={{ color: theme.mode === "light" ? "#0872B9" : "#FFF", padding: 4 }}>
                                        <CloseTicketIcon size={18} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {(ticket.status === "closed" && (ticket.queueId === null || ticket.queueId === undefined)) && (
                                <Tooltip title={`${i18n.t("ticketsList.buttons.reopen")}`}>
                                    <IconButton size="small" disabled={loading} onClick={() => handleOpenAcceptTicketWithouSelectQueue()} style={{ color: theme.mode === "light" ? "#0872B9" : "#FFF", padding: 4 }}>
                                        <AcceptIcon size={18} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {(ticket.status === "closed" && ticket.queueId !== null) && (
                                <Tooltip title={`${i18n.t("ticketsList.buttons.reopen")}`}>
                                    <IconButton size="small" disabled={loading} onClick={() => handleAcepptTicket(ticket.id)} style={{ color: theme.mode === "light" ? "#0872B9" : "#FFF", padding: 4 }}>
                                        <AcceptIcon size={18} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {/* <Divider variant="inset" component="li" /> */}
        </React.Fragment>
    );
};

export default TicketListItemCustom;
